import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ColorSwatch from "@/components/ColorSwatch";
import DateTimeQuarterInput from "@/components/DateTimeQuarterInput";

export default function Vehicles() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const pickup = params.get("pickup") || "";
  const dropoff = params.get("dropoff") || "";
  const office = params.get("office") || "";
  const [zonaNombre, setZonaNombre] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [cats, setCats] = useState(["Todos"]);
  const [cat, setCat] = useState("Todos");
  const [datesForm, setDatesForm] = useState({ pickup, dropoff });

  const hasDateFilter = useMemo(() => Boolean(pickup && dropoff), [pickup, dropoff]);

  useEffect(() => {
    axios.get("/api/categorias?visible_only=true").then(r => {
      setCats(["Todos", ...(r.data || []).map(c => c.nombre)]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!office) { setZonaNombre(""); return; }
    axios.get("/api/ubicaciones").then(r => {
      const u = (r.data || []).find(x => x.id === office);
      setZonaNombre(u ? u.nombre_zona : "");
    }).catch(() => {});
  }, [office]);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (office) qs.set("ubicacion_id", office);
    if (cat !== "Todos") qs.set("categoria", cat);
    if (pickup && dropoff) {
      qs.set("fecha_recogida", pickup);
      qs.set("fecha_devolucion", dropoff);
    }
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    axios.get(`/api/vehicles${tail}`).then(r => setVehicles(r.data || [])).catch(() => {});
  }, [cat, pickup, dropoff, office]);

  const applyDates = (e) => {
    e.preventDefault();
    
     if (
         datesForm.pickup &&
         datesForm.dropoff &&
         new Date(datesForm.dropoff) <= new Date(datesForm.pickup)
     ) {
         alert(t("vehicles.errorFechas"));
         return;
     }
    const next = new URLSearchParams(params);
    if (datesForm.pickup) next.set("pickup", datesForm.pickup); else next.delete("pickup");
    if (datesForm.dropoff) next.set("dropoff", datesForm.dropoff); else next.delete("dropoff");
    setParams(next);
  };

  const clearOffice = () => {
    const next = new URLSearchParams(params);
    next.delete("office");
    setParams(next);
  };

  const clearDates = () => {
    const next = new URLSearchParams(params);
    next.delete("pickup");
    next.delete("dropoff");
    setParams(next);
    setDatesForm({ pickup: "", dropoff: "" });
  };

  const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");

  return (
    <section className="section-light py-12" data-testid="vehicles-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <div className="divider-red mb-4"></div>
          <h1 className="font-display text-4xl sm:text-5xl">{t("vehicles.titulo")}</h1>
          <p className="text-neutral-600 mt-2">{t("vehicles.subtitulo")}</p>
        </div>

        {office && (
          <div className="mb-4 flex items-center gap-3 text-sm" data-testid="vehicles-zone-filter">
            <span className="px-3 py-1 bg-black text-white rounded-full">
              {t("vehicles.zona", { defaultValue: "Zona" })}: {zonaNombre || "…"}
            </span>
            <button type="button" onClick={clearOffice} className="underline text-neutral-600" data-testid="vehicles-zone-clear">
              {t("vehicles.verTodas", { defaultValue: "Ver todas las zonas" })}
            </button>
          </div>
        )}

        <form onSubmit={applyDates} className="bg-black text-white rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="vehicles-availability-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-neutral-400">{t("vehicles.recogida")}</label>
            <DateTimeQuarterInput
              value={datesForm.pickup}
              onChange={(pickup) => {
                setDatesForm(prev => ({
                  ...prev,
                  pickup,
                  dropoff: prev.dropoff && prev.dropoff < pickup ? pickup : prev.dropoff,
                }));
              }}
              dateTestId="filter-pickup-date" timeTestId="filter-pickup-time"
              placeholder={t("home.placeholderRecogida")}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-neutral-400">{t("vehicles.devolucion")}</label>
            <DateTimeQuarterInput
              value={datesForm.dropoff}
              onChange={(dropoff) => setDatesForm({...datesForm, dropoff})}
              min={datesForm.pickup || undefined}
              dateTestId="filter-dropoff-date" timeTestId="filter-dropoff-time"
              placeholder={t("home.placeholderDevolucion")}
            />
          </div>
          <button type="submit" className="btn-red py-3" data-testid="filter-apply">{t("vehicles.verDisponibles")}</button>
          {hasDateFilter && (
            <button type="button" onClick={clearDates} className="text-xs text-neutral-300 underline" data-testid="filter-clear">{t("vehicles.quitarFiltroFechas")}</button>
          )}
        </form>

        {hasDateFilter && (
          <div className="mb-6 px-4 py-3 bg-[var(--pc-red)] text-white rounded-lg text-sm" data-testid="active-filter-banner">
            {t("vehicles.mostrandoLibres")} <strong>{fmt(pickup)}</strong> {t("vehicles.y")} <strong>{fmt(dropoff)}</strong>.
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8" data-testid="vehicles-filters">
          {cats.map(c => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded text-sm uppercase tracking-wider font-semibold transition-colors ${
                cat === c ? "bg-[var(--pc-red)] text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
              data-testid={`filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>

        {vehicles.length === 0 && (
          <div className="text-neutral-500" data-testid="vehicles-empty">
            {hasDateFilter ? t("vehicles.sinCochesFechas") : t("vehicles.sinCochesCategoria")}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map(v => (
            <div key={v.id} className="car-card" data-testid={`vehicle-card-${v.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-neutral-800">
                <img src={v.imagen_url} alt={v.marca_modelo} className="w-full h-full object-cover" loading="lazy" onError={(e)=>{e.target.style.opacity=0.3;}}/>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <ColorSwatch color={v.color} size={14} testId={`swatch-${v.id}`} />
                    <h3 className="font-display text-xl leading-tight truncate">{v.marca_modelo}</h3>
                  </div>
                  <span className="km-badge shrink-0">{t("vehicles.kmDia")}</span>
                </div>
                <div className="text-xs uppercase tracking-wider text-neutral-400 mb-3">
                  {v.categoria} · {v.transmision} · {v.plazas} {t("vehicles.plazas")}{v.color ? ` · ${v.color}` : ""}
                </div>
                <div className="flex items-end justify-between mt-4">
                  <div>
                    <div className="text-3xl font-display text-[var(--pc-red)]">{(v.precio_dia === null || v.precio_dia === undefined || v.precio_dia === "" || !Number.isFinite(Number(v.precio_dia))) ? "—" : `${Number(v.precio_dia).toFixed(2)} €`}</div>
                    <div className="text-xs text-neutral-400">{t("vehicles.porDia")}</div>
                  </div>
                  <Link to={`/vehiculo/${v.id}${pickup && dropoff ? `?pickup=${encodeURIComponent(pickup)}&dropoff=${encodeURIComponent(dropoff)}` : ""}`} className="btn-red px-4 py-2 text-sm" data-testid={`view-details-${v.id}`}>{t("vehicles.reservar")}</Link>                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
